import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useSqlMiCalc } from '../../hooks/useCalculate'
import { useAnalytics } from '../../hooks/useAnalytics'
import type { SqlMiRequest } from '../../api/types'
import { TrustBar } from '../shared/TrustBar'
import { ShareButton } from '../shared/ShareButton'
import { FieldTooltip } from '../shared/FieldTooltip'
import { ScenarioPresets } from '../shared/ScenarioPresets'

type Tier = 'gp' | 'bc'
type Reservation = 'none' | 'one_year' | 'three_year'

const TIER_LABELS: Record<Tier, string> = {
  gp: 'General Purpose (Gen5)',
  bc: 'Business Critical (Gen5)',
}

const RESERVATION_LABELS: Record<Reservation, string> = {
  none: 'Pay-as-you-go',
  one_year: '1-year Reserved (~33% saving)',
  three_year: '3-year Reserved (~55% saving)',
}

const VCORE_OPTIONS = [4, 8, 16, 24, 32, 40, 64, 80]

function fmt(n: number) {
  return n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function SelectField({ label, value, options, onChange, hint, tooltipContent }: {
  label: string
  value: string
  options: { value: string; label: string }[]
  onChange: (v: string) => void
  hint?: string
  tooltipContent?: string
}) {
  return (
    <div>
      <label className="block text-[13px] font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
        {label}{tooltipContent && <FieldTooltip content={tooltipContent} />}
      </label>
      {hint && <p className="text-[11px] mb-1" style={{ color: 'var(--text-tertiary)' }}>{hint}</p>}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded px-3 py-2 text-[13px] border"
        style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)', color: 'var(--text-primary)' }}
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  )
}

function NumInput({ label, value, onChange, min = 0, max, step = 1, hint }: {
  label: string; value: number; onChange: (v: number) => void
  min?: number; max?: number; step?: number; hint?: string
}) {
  return (
    <div>
      <label className="block text-[13px] font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>{label}</label>
      {hint && <p className="text-[11px] mb-1" style={{ color: 'var(--text-tertiary)' }}>{hint}</p>}
      <input
        type="number" value={value} min={min} max={max} step={step}
        onChange={(e) => onChange(Math.max(min, Number(e.target.value)))}
        className="w-full rounded px-3 py-2 text-[13px] border"
        style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)', color: 'var(--text-primary)' }}
      />
    </div>
  )
}

function Toggle({ label, checked, onChange, hint, tooltipContent }: {
  label: string; checked: boolean; onChange: (v: boolean) => void; hint?: string; tooltipContent?: string
}) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <div className="relative mt-0.5">
        <input
          type="checkbox" checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="sr-only"
        />
        <div
          className="w-10 h-6 rounded-full transition-colors"
          style={{ backgroundColor: checked ? '#0078d4' : 'var(--outline-variant)' }}
        />
        <div
          className="absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform"
          style={{ transform: checked ? 'translateX(16px)' : 'translateX(0)' }}
        />
      </div>
      <div>
        <span className="text-[13px] font-medium" style={{ color: 'var(--text-secondary)' }}>{label}{tooltipContent && <FieldTooltip content={tooltipContent} />}</span>
        {hint && <p className="text-[11px] mt-0.5" style={{ color: 'var(--text-tertiary)' }}>{hint}</p>}
      </div>
    </label>
  )
}

export default function SqlMiCalc() {
  const [tier, setTier] = useState<Tier>('gp')
  const [vCores, setVCores] = useState(8)
  const [storageGb, setStorageGb] = useState(256)
  const [backupStorageGb, setBackupStorageGb] = useState(0)
  const [ahbEnabled, setAhbEnabled] = useState(false)
  const [reservation, setReservation] = useState<Reservation>('none')

  const inputs: SqlMiRequest = { tier, vCores, storageGb, backupStorageGb, ahbEnabled, reservation }
  const { data: result, isLoading, isError } = useSqlMiCalc(inputs)
  const { trackCalculatorUsed } = useAnalytics()

  useEffect(() => {
    if (result?.totalMonthly) {
      trackCalculatorUsed({ calculator: 'sql-managed-instance', result_gbp: result.totalMonthly, tier })
    }
  }, [result?.totalMonthly])

  const includedStorage = vCores * 32

  return (
    <div className="space-y-6">
      <TrustBar />

      <ScenarioPresets presets={[
        {
          label: 'General Purpose (dev)',
          description: 'GP · 4 vCores · 128 GB storage · no discounts — minimum viable SQL MI',
          apply: () => { setTier('gp'); setVCores(4); setStorageGb(128); setAhbEnabled(false); setReservation('none'); },
        },
        {
          label: 'General Purpose + AHB',
          description: 'GP · 8 vCores · 256 GB · Azure Hybrid Benefit (~30% compute saving)',
          apply: () => { setTier('gp'); setVCores(8); setStorageGb(256); setAhbEnabled(true); setReservation('none'); },
        },
        {
          label: 'Business Critical (HA)',
          description: 'BC · 8 vCores · 512 GB · AHB + 1yr RI — built-in Always On replica',
          apply: () => { setTier('bc'); setVCores(8); setStorageGb(512); setAhbEnabled(true); setReservation('one_year'); },
        },
      ]} />

      {/* Section 1: Instance configuration */}
      <div className="p-5 rounded-lg space-y-4" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
        <h2 className="text-[14px] font-semibold" style={{ color: 'var(--text-primary)' }}>Instance configuration</h2>

        <SelectField
          label="Service tier"
          tooltipContent="General Purpose: standard compute on remote storage. Business Critical: local SSD, higher IOPS, built-in read replica."
          value={tier}
          onChange={(v) => setTier(v as Tier)}
          options={Object.entries(TIER_LABELS).map(([v, l]) => ({ value: v, label: l }))}
          hint="General Purpose is compute-optimised for most workloads. Business Critical adds local SSD, read replicas, and higher IOPS."
        />

        <SelectField
          label="vCores"
          tooltipContent="Virtual cores allocated to the instance. More vCores = more CPU capacity and higher cost. Scales independently of storage."
          value={String(vCores)}
          onChange={(v) => setVCores(Number(v))}
          options={VCORE_OPTIONS.map(n => ({ value: String(n), label: `${n} vCores (${n * 32} GB included storage)` }))}
          hint="Each vCore includes 32 GB of storage at no extra charge."
        />
      </div>

      {/* Section 2: Storage */}
      <div className="p-5 rounded-lg space-y-4" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
        <h2 className="text-[14px] font-semibold" style={{ color: 'var(--text-primary)' }}>Storage &amp; backup</h2>

        <NumInput
          label="Total storage (GB)"
          value={storageGb}
          onChange={setStorageGb}
          min={32}
          max={16384}
          step={32}
          hint={`${includedStorage} GB included with ${vCores} vCores. Extra storage charged at ${tier === 'gp' ? '£0.0995' : '£0.2167'}/GB/month.`}
        />

        <NumInput
          label="PITR backup storage (GB)"
          value={backupStorageGb}
          onChange={setBackupStorageGb}
          min={0}
          max={100000}
          step={50}
          hint="LRS backup storage at £0.0865/GB/month. Some backup is included — enter only the amount above your database size."
        />
      </div>

      {/* Section 3: Licensing & reservations */}
      <div className="p-5 rounded-lg space-y-4" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
        <h2 className="text-[14px] font-semibold" style={{ color: 'var(--text-primary)' }}>Licensing &amp; commitment</h2>

        <Toggle
          label="Azure Hybrid Benefit (AHB)"
          tooltipContent="Use your existing on-premises SQL Server licences with Software Assurance to save ~40% on the licence component of the cost."
          checked={ahbEnabled}
          onChange={setAhbEnabled}
          hint={`Use existing SQL Server licences with Software Assurance. ~${tier === 'bc' ? '50%' : '30%'} compute saving.`}
        />

        <SelectField
          label="Reservation"
          value={reservation}
          onChange={(v) => setReservation(v as Reservation)}
          options={Object.entries(RESERVATION_LABELS).map(([v, l]) => ({ value: v, label: l }))}
          hint="Reserved Instances apply to compute only. RI and AHB discounts stack multiplicatively."
        />
      </div>

      {/* Result panel */}
      {isLoading && (
        <div className="p-5 rounded-lg text-center text-[13px]" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)', color: 'var(--text-tertiary)' }}>
          Calculating…
        </div>
      )}
      {isError && (
        <div className="p-5 rounded-lg text-[13px]" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)', color: '#a4262c' }}>
          Could not fetch pricing. Check your inputs and try again.
        </div>
      )}
      {result && !isLoading && (
        <div className="p-5 rounded-lg space-y-4" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
          <div className="flex items-baseline justify-between">
            <h2 className="text-[14px] font-semibold" style={{ color: 'var(--text-primary)' }}>Estimated monthly cost (PAYG)</h2>
            <span className="text-[28px] font-bold" style={{ color: '#0078d4' }}>£{fmt(result.totalMonthly)}</span>
          </div>

          <div className="space-y-2 pt-2" style={{ borderTop: '1px solid var(--outline-variant)' }}>
            <div className="flex justify-between text-[13px]">
              <span style={{ color: 'var(--text-secondary)' }}>
                Compute ({vCores} vCores · {TIER_LABELS[tier]}{ahbEnabled ? ' · AHB' : ''}{reservation !== 'none' ? ` · ${reservation === 'one_year' ? '1yr RI' : '3yr RI'}` : ''})
              </span>
              <span style={{ color: 'var(--text-primary)' }}>£{fmt(result.computeMonthly)}</span>
            </div>
            {result.storageMonthly > 0 && (
              <div className="flex justify-between text-[13px]">
                <span style={{ color: 'var(--text-secondary)' }}>
                  Extra storage ({result.extraStorageGb} GB above {result.includedStorageGb} GB included)
                </span>
                <span style={{ color: 'var(--text-primary)' }}>£{fmt(result.storageMonthly)}</span>
              </div>
            )}
            {result.storageMonthly === 0 && (
              <div className="flex justify-between text-[13px]">
                <span style={{ color: 'var(--text-secondary)' }}>
                  Storage ({storageGb} GB — within {result.includedStorageGb} GB included)
                </span>
                <span style={{ color: 'var(--text-primary)' }}>Included</span>
              </div>
            )}
            {result.backupMonthly > 0 && (
              <div className="flex justify-between text-[13px]">
                <span style={{ color: 'var(--text-secondary)' }}>PITR backup storage ({backupStorageGb} GB LRS)</span>
                <span style={{ color: 'var(--text-primary)' }}>£{fmt(result.backupMonthly)}</span>
              </div>
            )}
          </div>

          {result.pricesLastUpdated && (
            <p className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
              Compute rates from D1 — last updated {result.pricesLastUpdated.split(' ')[0]}
            </p>
          )}

          <div className="flex gap-2 pt-2">
            <ShareButton inputs={inputs} result={result} service="sql-managed-instance" />
          </div>

          <a href="https://azure.microsoft.com/en-gb/pricing/details/azure-sql-managed-instance/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-[10px] font-mono mt-1" style={{ color: 'var(--text-tertiary)' }}>
            <span className="material-symbols-outlined text-[13px]">open_in_new</span>
            Verify on official Azure pricing page
          </a>

          <p className="text-[11px] pt-1" style={{ color: 'var(--text-tertiary)' }}>{result.disclaimer}</p>
        </div>
      )}

      {/* SQL MI vs SQL DB comparison */}
      <div className="p-5 rounded-lg" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
        <h2 className="text-[14px] font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
          SQL Managed Instance vs SQL Database
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--outline-variant)' }}>
                <th className="text-left py-2 pr-4 font-medium" style={{ color: 'var(--text-secondary)' }}>Feature</th>
                <th className="text-left py-2 pr-4 font-medium" style={{ color: '#0078d4' }}>Managed Instance</th>
                <th className="text-left py-2 font-medium" style={{ color: 'var(--text-secondary)' }}>SQL Database</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['Migration effort', 'Near-zero (100% SQL Server compat)', 'May require code changes'],
                ['SQL Server Agent', 'Yes', 'No'],
                ['Cross-database queries', 'Yes', 'Limited'],
                ['Minimum cost', '~£836/month (GP 8 vCore PAYG)', '~£4/month (Basic)'],
                ['Serverless option', 'No', 'Yes (GP Serverless)'],
                ['Max database size', '16 TB', '4 TB (GP) / 1 TB (BC)'],
                ['SLA', '99.99%', '99.99%'],
              ].map(([feat, mi, db]) => (
                <tr key={feat} style={{ borderBottom: '1px solid var(--outline-variant)' }}>
                  <td className="py-2 pr-4 font-medium" style={{ color: 'var(--text-secondary)' }}>{feat}</td>
                  <td className="py-2 pr-4" style={{ color: 'var(--text-primary)' }}>{mi}</td>
                  <td className="py-2" style={{ color: 'var(--text-tertiary)' }}>{db}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Billing traps */}
      <div className="p-5 rounded-lg" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
        <h2 className="text-[14px] font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
          <span className="material-symbols-outlined text-[#e8b000] text-[16px] align-middle mr-1">warning</span>
          SQL MI billing traps
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            {
              title: 'Minimum 4 vCores',
              body: 'The minimum viable SQL MI is 4 vCores GP at ~£418/month PAYG. There is no serverless or Basic tier — it is always enterprise-grade.',
            },
            {
              title: 'Storage is per-instance, not per-GB',
              body: 'Storage is provisioned in blocks. Each vCore includes 32 GB. You pay for provisioned capacity, not consumed capacity.',
            },
            {
              title: 'RI applies to compute only',
              body: 'A 3-year RI does not reduce storage or backup costs. For a storage-heavy workload, RI savings may be less impactful than expected.',
            },
            {
              title: 'Business Critical minimum is high',
              body: 'BC tier starts at 4 vCores (~£836/month PAYG). AHB + 3yr RI can reduce this ~65%, but the baseline commitment is significant.',
            },
          ].map(trap => (
            <div key={trap.title} className="p-3 rounded" style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--outline-variant)' }}>
              <p className="text-[13px] font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>{trap.title}</p>
              <p className="text-[12px]" style={{ color: 'var(--text-secondary)' }}>{trap.body}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Link
          to="/guides/azure-sql-managed-instance-pricing-uk"
          className="flex items-center gap-3 p-4 rounded transition-colors group"
          style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
        >
          <span className="material-symbols-outlined text-[#0078d4] text-[20px]" style={{ fontVariationSettings: "'FILL' 1" }}>menu_book</span>
          <div>
            <p className="text-[14px] font-semibold group-hover:text-[#0078d4] transition-colors" style={{ color: 'var(--text-secondary)' }}>Azure SQL Managed Instance Pricing Guide UK</p>
            <p className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>AHB, Reserved Instances, tier selection, and migration cost planning</p>
          </div>
          <span className="material-symbols-outlined text-[#0078d4] text-[16px] ml-auto">arrow_forward</span>
        </Link>
        <Link
          to="/sql-database"
          className="flex items-center gap-3 p-4 rounded transition-colors group"
          style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
        >
          <span className="material-symbols-outlined text-[#0078d4] text-[20px]" style={{ fontVariationSettings: "'FILL' 1" }}>database</span>
          <div>
            <p className="text-[14px] font-semibold group-hover:text-[#0078d4] transition-colors" style={{ color: 'var(--text-secondary)' }}>Azure SQL Database Calculator</p>
            <p className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>Compare DTU vs vCore, Serverless, and Hyperscale tiers</p>
          </div>
          <span className="material-symbols-outlined text-[#0078d4] text-[16px] ml-auto">arrow_forward</span>
        </Link>
      </div>
    </div>
  )
}
